import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { AnimatePresence } from "framer-motion";
import { GetStaticPropsContext } from "next";
import { useSession } from "next-auth/react";
import { QueryClient } from "@tanstack/react-query";
import { TournamentOverview } from "../../queries/tournaments";
import TournamentPageTabs from "../../components/TournamentPageTabs/TournamentPageTabs";
import { useUI } from "../../context/UIContext";
import Overview from "../../components/TournamentPageTabs/Overview";
import CourseAndSettings from "../../components/TournamentPageTabs/CourseAndSettings";
import Purse from "../../components/TournamentPageTabs/Purse";
import LeaderboardWrapper from "../../components/LeaderboardWrapper/LeaderboardWrapper";
import GridWrapper from "../../components/GridWrapper/GridWrapper";
import GridElement from "../../components/GridElement/GridElement";
import BackgroundGraphic from "../../components/BackgroundGraphic/BackgroundGraphic";
import PrizeListSeason1 from "../../components/PrizeList/PrizeListSeason1";
import {
  isFinalSeason1,
  isNowPlayingTournament,
  groupTournamentsBySeason,
} from "../../utils/tournaments";
import PrizeList from "../../components/PrizeList/PrizeList";
import { Tournament } from "../../types/tournament";
import JoinFlowInfoModal from "../../components/JoinFlowInfoModal/JoinFlowInfoModal";
import { useTmSignOut } from "../../hooks/useTmSignOut";
import LeaderboardModal from "../../components/LeaderboardModal/LeaderboardModal";
import Head from "next/head";
import { getStoryblokApi, useStoryblokState } from "@storyblok/react";
import { Tab } from "../../types/tabs";
import {
  getAccessToken,
  getTournamentNationalities,
  getTournamentOverview,
  getTournamentOverviewList,
  getTournamentRoundDetails,
} from "../../utils/react-query-utils";
import { CountryCodesMap } from "../../utils/county-codes-map";
import { PGA_TOURNAMENT_ID } from "../../utils/constants";
import Earnings from "../../components/TournamentPageTabs/Earnings";
import fs from "fs";
import path from "path";
import Papa from "papaparse";
import YouTubePlaylist from "../../components/YouTubePlaylist";
import { fetchYoutubeVideos } from "../../utils/youtube-playlist";

interface CsvRow {
  [key: string]: string | number;
}

interface CsvFile {
  name: string;
  data: CsvRow[];
}

const TournamentPageHead = ({
  metaTitle,
  isScoreCard,
  isProd,
}: {
  metaTitle: string;
  isScoreCard?: boolean;
  isProd: boolean;
}) => {
  const title = isScoreCard ? "View My Scorecard - NEXT Golf Tour" : metaTitle;
  const description = isScoreCard
    ? "Dive into the action with video and detailed hole-by-hole scores from my round in the NEXT Golf Tour."
    : "The NEXT Golf Tour: Professional golf with real stakes played in a Trackman golf simulator. Experience the skill, intensity, and thrill of tournament play.";
  return (
    <Head>
      {!isProd && <meta name="robots" content="noindex, nofollow" />}
      <title>{title}</title>
      <link rel="icon" href="/favicon.ico" />
      <meta
        name="viewport"
        content="initial-scale=1.0, width=device-width, maximum-scale=1.0, user-scalable=0"
        key="viewport"
      />
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content="/images/next_tournament_s3.jpg" />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content="/images/next_tournament_s3.jpg" />
    </Head>
  );
};

export default function TournamentOverviewPage({
  story,
  tournament,
  courseSettings,
  pageSlug,
  isProd,
  tabs,
  nationalitiesOptions,
  csvFiles,
  videos,
  videosTemplate,
  nextPageToken,
  playlistId,
}: any) {
  story = useStoryblokState(story);

  const { state: uiState } = useUI();
  const { data: session }: { data: any; status: string } = useSession();

  const [verifyEmailFlow, setVerifyEmailFlow] = useState<string | null>("");

  const {
    asPath,
    query: { playerid },
  } = useRouter();

  useEffect(() => {
    if (verifyEmailFlow !== window.localStorage.getItem("verify_email_flow")) {
      setVerifyEmailFlow(window.localStorage.getItem("verify_email_flow"));
    }
  }, [uiState]);

  const {
    name,
    season,
    rounds,
    startDate,
    endDate,
    sponsors,
    participants,
    id: tournamentId,
  } = tournament ?? {};

  const playersCount =
    participants?.allInvitationsCount -
    participants?.declinedCount -
    participants?.withdrawnCount;

  const isPGATournament = tournamentId === PGA_TOURNAMENT_ID;
  const isFinalSeason3 = season === 3 && name?.toLowerCase()?.includes("final");

  useTmSignOut();

  useEffect(() => {
    if (typeof document !== "undefined") {
      if (
        playerid &&
        typeof playerid === "string" &&
        pageSlug === "leaderboard"
      ) {
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
      } else {
        document.body.style.overflow = "auto";
        document.body.style.position = "static";
        document.body.style.width = "unset";
      }
    }
  }, [playerid, pageSlug]);

  if (pageSlug === "prize-list") {
    return (
      <>
        <TournamentPageHead metaTitle={name} isProd={isProd} />
        {season === 1 && (
          <PrizeListSeason1
            defaultSpacing={"x_large"}
            tournamentId={tournamentId}
          />
        )}
        {(season === 2 || season === 3) && (
          <PrizeList
            tournament={tournament}
            isPGATournament={isPGATournament}
          />
        )}
      </>
    );
  }

  const csvFileByTournament = csvFiles.find(
    //TODO: rework after season 3 has all csv
    (file: { name: string | string[] }) => {
      if (isPGATournament) {
        return file.name.includes("S3") && file.name.includes("PGA");
      }

      if (tournament.name.trim().toLowerCase().includes("round 1")) {
        return file.name.includes("S3") && file.name.includes("R1");
      }

      if (tournament.name.trim().toLowerCase().includes("round 2")) {
        return file.name.includes("S3") && file.name.includes("R2");
      }

      if (tournament.name.trim().toLowerCase().includes("round 3")) {
        return file.name.includes("S3") && file.name.includes("R3");
      }

      if (tournament.name.trim().toLowerCase().includes("final")) {
        return file.name.includes("S3") && file.name.includes("R4");
      }
      return;
    }
  );

  return (
    <>
      <TournamentPageHead
        metaTitle={name}
        isScoreCard={asPath.includes("playerid")}
        isProd={isProd}
      />
      <JoinFlowInfoModal
        isOpen={
          uiState.joinFlowInfoModal.isOpen &&
          ((session?.accessToken &&
            !uiState.isEmailVerified &&
            verifyEmailFlow === "true" &&
            uiState.joinFlowInfoModal.modalType === "email") ||
            uiState.joinFlowInfoModal.modalType === "signup")
        }
        tmToken={session?.accessToken}
        idTokenHint={session?.idToken}
      />
      <TournamentPageTabs
        tournamentId={tournament?.id}
        signUpStartTime={tournament.signUpStartTime}
        signUpEndTime={tournament.signUpEndTime}
        playersCount={playersCount}
        signupUri={
          !!tournament?.locationsConfiguration?.length &&
          tournament?.locationsConfiguration[0].paymentUrl
        }
        logo={sponsors?.[0]?.logoUri}
        name={name}
        tabs={tabs}
        isPGATournament={isPGATournament}
        isEarnings={!!csvFileByTournament}
        isFinalSeason3={isFinalSeason3}
      />
      {pageSlug !== "leaderboard" && (
        <GridWrapper style={{ marginBottom: "50px" }}>
          <GridElement gridLayout={[[1401, 4, 26]]}>
            {pageSlug === "" && (
              <Overview
                configurationInfo={tournament?.locationsConfiguration}
                startDate={startDate}
                endDate={endDate}
                courseName={rounds?.[0]?.course?.displayName}
                playersCount={playersCount}
                isFinalSeason1={isFinalSeason1(tournamentId)}
                tournamentName={name}
                tournamentSeason={season}
                isPGATournament={isPGATournament}
                signUpStartTime={tournament.signUpStartTime}
                signUpEndTime={tournament.signUpEndTime}
              />
            )}
            {pageSlug === "course-and-settings" && (
              <CourseAndSettings
                settings={courseSettings?.round?.settings}
                course={courseSettings?.round?.course}
                selectedHoles={
                  courseSettings?.rounds?.[0]?.embeddedGame?.longestDrive?.holes
                }
                tournamentId={tournamentId}
                tournamentName={name}
                tournamentSeason={season}
              />
            )}
            {pageSlug === "purse" && (
              <Purse
                playersCount={playersCount}
                tournamentName={name}
                isFinalSeason1={isFinalSeason1(tournamentId)}
                tournamentId={tournamentId}
                isPGATournament={isPGATournament}
                tournamentSeason={season}
              />
            )}
            {pageSlug === "earnings" && (
              <Earnings csvFile={csvFileByTournament?.data} />
            )}
          </GridElement>
        </GridWrapper>
      )}
      {pageSlug === "leaderboard" && (
        <>
          <div style={{ marginBottom: "50px" }}>
            <BackgroundGraphic />
            {videos && videos.length > 0 && (
              <YouTubePlaylist
                videos={videos}
                videosTemplate={videosTemplate}
                nextPageToken={nextPageToken}
                playlistId={playlistId}
              />
            )}
            <LeaderboardWrapper
              tournamentSeason={season}
              isNowPlaying={isNowPlayingTournament(startDate, endDate)}
              startDate={startDate}
              tournamentId={tournamentId}
              tournamentName={name}
              // isFinale={isFinalRound(season, name)}
              nationalitiesOptions={nationalitiesOptions}
              ctpSelectOptions={
                tournament?.rounds[0]?.embeddedGame?.closestToPin?.holes
              }
              isPGATournament={isPGATournament}
            />
          </div>
          <AnimatePresence>
            {playerid && typeof playerid === "string" && (
              <LeaderboardModal
                tournamentId={tournamentId}
                playerId={playerid}
                // isFinale={isFinalRound(
                //   name
                // )}
              />
            )}
          </AnimatePresence>
        </>
      )}
    </>
  );
}

export async function getStaticPaths() {
  let pastTournaments = [];
  let activeTournaments = [];

  const accessToken = await getAccessToken({
    processEnv: process.env.VERCEL_ENV,
    clientId: process.env.TFM_PORTAL_CLIENT_ID,
    clientIdDev: process.env.TFM_PORTAL_CLIENT_ID_DEV,
    clientSecret: process.env.TFM_PORTAL_CLIENT_SECRET,
    clientSecretDev: process.env.TFM_PORTAL_CLIENT_SECRET_DEV,
  });

  try {
    const queryClient = new QueryClient();
    const { data: pastTournamentList } = await queryClient.fetchQuery(
      ["tournament_overview_list"],
      () =>
        getTournamentOverviewList({
          list: "HISTORY",
          processEnv: process.env.VERCEL_ENV,
          accessToken,
        })
    );
    pastTournaments =
      pastTournamentList?.data?.node?.tournaments?.list?.items || [];

    const { data: activeTournamentList } = await queryClient.fetchQuery(
      ["tournament_overview_list"],
      () =>
        getTournamentOverviewList({
          list: "LIVE_AND_UPCOMING",
          processEnv: process.env.VERCEL_ENV,
          accessToken,
        })
    );
    activeTournaments =
      activeTournamentList?.data?.node?.tournaments?.list?.items || [];
  } catch (err: any) {
    console.log(`===> error: ${err.message}`);
  }

  const proTournaments = [...pastTournaments, ...activeTournaments].filter(
    (tournament: Tournament) => {
      return tournament?.keyValues?.find((keyValue) => {
        return process.env.VERCEL_ENV === "production"
          ? keyValue.key === "NextTour:TournamentKind" &&
              keyValue.value.toLowerCase() === "pro"
          : keyValue.key === "NextTour:TournamentKind" &&
              (keyValue.value.toLowerCase() === "test" ||
                keyValue.value.toLowerCase() === "pro");
      });
    }
  );

  const paths = proTournaments
    ? proTournaments
        ?.filter((item: { id: string }) => item.id)
        ?.map((item: { name: string; id: string }) => {
          return {
            params: {
              slug: [
                item.name
                  .replace(/:/g, "")
                  .replace(/ - /g, " ")
                  .replace(/\//g, "-")
                  .replace(/#/g, "")
                  .split(" ")
                  .join("-")
                  .toLowerCase(),
              ],
            },
          };
        })
    : [];

  const sbParams = {
    version: process.env.VERCEL_ENV === "production" ? "published" : "draft",
    resolve_links: "url",
  };

  const storyblokApi = getStoryblokApi();
  const { data: tournamentStoriesData } = await storyblokApi.get(
    `cdn/stories/`,
    {
      ...sbParams,
      starts_with: "tournament",
    }
  );

  const tabs = tournamentStoriesData?.stories?.map((story: any) => ({
    value: story?.name,
    label: story?.name,
    slug: story?.slug,
  }));

  const fullPaths: { params: { slug: string[] } }[] = [];
  paths.forEach((path) => {
    fullPaths.push(path);
    tabs.forEach((tab: Tab) => {
      if (!!tab.slug) {
        fullPaths.push({
          params: { slug: [...path.params.slug, tab.slug] },
        });
      }
    });
    fullPaths.push({
      params: { slug: [...path.params.slug, "prize-list"] },
    });
  });

  return {
    paths: fullPaths || [],
    fallback: "blocking",
  };
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const queryClient = new QueryClient();
  let pastTournaments = [];
  let activeTournaments = [];

  const accessToken = await getAccessToken({
    processEnv: process.env.VERCEL_ENV,
    clientId: process.env.TFM_PORTAL_CLIENT_ID,
    clientIdDev: process.env.TFM_PORTAL_CLIENT_ID_DEV,
    clientSecret: process.env.TFM_PORTAL_CLIENT_SECRET,
    clientSecretDev: process.env.TFM_PORTAL_CLIENT_SECRET_DEV,
  });

  try {
    const { data: pastTournamentList } = await queryClient.fetchQuery(
      ["tournament_overview_list"],
      () =>
        getTournamentOverviewList({
          list: "HISTORY",
          processEnv: process.env.VERCEL_ENV,
          accessToken,
        })
    );
    pastTournaments =
      pastTournamentList?.data?.node?.tournaments?.list?.items || [];

    const { data: activeTournamentList } = await queryClient.fetchQuery(
      ["tournament_overview_list"],
      () =>
        getTournamentOverviewList({
          list: "LIVE_AND_UPCOMING",
          processEnv: process.env.VERCEL_ENV,
          accessToken,
        })
    );
    activeTournaments =
      activeTournamentList?.data?.node?.tournaments?.list?.items || [];
  } catch (err: any) {
    console.log(`===> error: ${err.message}`);
  }

  const proTournaments = [...pastTournaments, ...activeTournaments].filter(
    (tournament: Tournament) => {
      return tournament?.keyValues?.find((keyValue) => {
        return process.env.VERCEL_ENV === "production"
          ? keyValue.key === "NextTour:TournamentKind" &&
              keyValue.value.toLowerCase() === "pro"
          : keyValue.key === "NextTour:TournamentKind" &&
              (keyValue.value.toLowerCase() === "test" ||
                keyValue.value.toLowerCase() === "pro");
      });
    }
  );

  const groupedTournamentsBySeason = groupTournamentsBySeason(proTournaments);

  const findTournament =
    groupedTournamentsBySeason &&
    groupedTournamentsBySeason.find(
      (item: { id: string; name: string }) =>
        item.name &&
        item.id &&
        context?.params?.slug?.[0] &&
        item.name
          .replace(/:/g, "")
          .replace(/ - /g, " ")
          .split(" ")
          .join("-")
          .toLowerCase() === context?.params?.slug[0]
    );

  let tournamentData: any;
  try {
    const { data } = await queryClient.fetchQuery(["tournament_overview"], () =>
      getTournamentOverview({
        processEnv: process.env.VERCEL_ENV,
        tournamentId: findTournament?.id || "",
        query: TournamentOverview,
        accessToken,
      })
    );
    tournamentData = data;
  } catch (err: any) {
    console.log(`===> error: ${err.message}`);
  }

  let roundDetailsData: any;
  try {
    const { data } = await queryClient.fetchQuery(
      ["tournament_round_details"],
      () =>
        getTournamentRoundDetails({
          processEnv: process.env.VERCEL_ENV,
          tournamentId: findTournament?.id || "",
          roundId: findTournament?.rounds[0]?.id || "",
          accessToken,
        })
    );
    roundDetailsData = data;
  } catch (err: any) {
    console.log(`===> error: ${err.message}`);
  }

  const sbParams = {
    version: process.env.VERCEL_ENV === "production" ? "published" : "draft",
    resolve_links: "url",
  };

  const storyblokApi = getStoryblokApi();
  const { data: tournamentStoriesData } = await storyblokApi.get(
    `cdn/stories/`,
    {
      ...sbParams,
      starts_with: "tournament",
    }
  );

  const tabs = tournamentStoriesData?.stories?.map((story: any) => ({
    value: story?.name,
    label: story?.name,
    slug: story?.slug,
  }));

  let nationalitiesData: any;
  try {
    const { data } = await queryClient.fetchQuery(
      ["tournament_nationalities"],
      () =>
        getTournamentNationalities({
          processEnv: process.env.VERCEL_ENV,
          tournamentId: findTournament?.id || "",
          accessToken,
        })
    );

    nationalitiesData = data;
  } catch (err: any) {
    console.log(`===> error: ${err.message}`);
  }

  const nationalitiesOptions =
    nationalitiesData?.data?.node?.participants?.demographics?.nationalities
      ?.map((item: string) => {
        return {
          label: CountryCodesMap[item as keyof typeof CountryCodesMap],
          value: item,
        };
      })
      .sort((a: { label: string }, b: { label: string }) =>
        a?.label?.localeCompare(b?.label)
      );

  const fullTournamentData = {
    ...findTournament,
    ...tournamentData?.data?.node,
  };

  const { data: leaderboardStoryData } = await storyblokApi.get(
    "cdn/stories/tournament/leaderboard",
    sbParams
  );

  const publicPath = path.join(process.cwd(), "public/csv/earnings");
  const files = fs
    .readdirSync(publicPath)
    .filter((file) => file.endsWith(".csv"));

  const csvFiles: CsvFile[] = files.map((file) => {
    const filePath = path.join(publicPath, file);
    const csvText = fs.readFileSync(filePath, "utf-8");

    let parsedData: CsvRow[] = [];
    Papa.parse(csvText, {
      header: false,
      skipEmptyLines: true,
      dynamicTyping: true,
      complete: (result) => {
        parsedData = result?.data as CsvRow[];
      },
    });

    return {
      name: file,
      data: parsedData,
    };
  });

  const youTubePlaylistData = leaderboardStoryData.story.content.body.find(
    (body_item: any) => body_item.component === "youtube_playlist"
  );
  const youtubePlaylistID =
    youTubePlaylistData?.tournament_playlist_table?.tbody
      ?.map((tableRow: any) =>
        tableRow.body[0].value === fullTournamentData.id
          ? tableRow.body[1].value
          : ""
      )
      .filter((el: string) => el)?.[0] || "";

  const youtubePlaylistTemplate = youTubePlaylistData?.videos_template;
  const youtubeResponse = await fetchYoutubeVideos(
    youtubePlaylistID,
    youtubePlaylistTemplate
  );

  return {
    props: {
      story: leaderboardStoryData ? leaderboardStoryData.story : false,
      isProd: process.env.VERCEL_ENV === "production",
      tournament: fullTournamentData || null,
      courseSettings: roundDetailsData?.data?.node || null,
      pageSlug:
        context?.params?.slug?.length && context?.params?.slug?.length > 1
          ? context.params.slug[context.params.slug.length - 1]
          : "",
      tabs,
      nationalitiesOptions:
        nationalitiesOptions?.length > 0
          ? JSON.parse(JSON.stringify(nationalitiesOptions))
          : null,
      csvFiles,
      videos: youtubeResponse.videos,
      videosTemplate: youtubePlaylistTemplate,
      nextPageToken: youtubeResponse.nextPageToken || null,
      playlistId: youtubePlaylistID,
    },
    notFound: !tournamentData?.data?.node,
    revalidate: 10,
  };
}
